"use client";

import { useLocale } from "next-intl";
import { useEffect, useId, useRef, useState, useTransition } from "react";
import { ChevronDown, Check } from "lucide-react";
import { usePathname, useRouter } from "@/i18n/navigation";
import { routing } from "@/i18n/routing";
import { track } from "@/lib/analytics";
import { cn } from "@/lib/utils";

const NAMES: Record<string, string> = { nl: "Nederlands", en: "English" };

function Flag({ locale, className }: { locale: string; className?: string }) {
  const uid = useId().replace(/:/g, "");
  if (locale === "nl") {
    return (
      <svg viewBox="0 0 9 6" preserveAspectRatio="xMidYMid slice" className={className} aria-hidden="true">
        <rect width="9" height="6" fill="#21468B" />
        <rect width="9" height="4" fill="#FFFFFF" />
        <rect width="9" height="2" fill="#AE1C28" />
      </svg>
    );
  }
  // English -> British flag (Union Jack)
  return (
    <svg viewBox="0 0 60 30" preserveAspectRatio="xMidYMid slice" className={className} aria-hidden="true">
      <clipPath id={`b-${uid}`}>
        <rect width="60" height="30" />
      </clipPath>
      <clipPath id={`d-${uid}`}>
        <path d="M30,15 30,0 60,0 z M30,15 60,15 60,30 z M30,15 30,30 0,30 z M30,15 0,15 0,0 z" />
      </clipPath>
      <g clipPath={`url(#b-${uid})`}>
        <rect width="60" height="30" fill="#012169" />
        <path d="M0,0 60,30 M60,0 0,30" stroke="#FFFFFF" strokeWidth="6" />
        <path d="M0,0 60,30 M60,0 0,30" clipPath={`url(#d-${uid})`} stroke="#C8102E" strokeWidth="4" />
        <path d="M30,0 30,30 M0,15 60,15" stroke="#FFFFFF" strokeWidth="10" />
        <path d="M30,0 30,30 M0,15 60,15" stroke="#C8102E" strokeWidth="6" />
      </g>
    </svg>
  );
}

export function LanguageSwitcher({
  className,
  dark = false,
  align = "right",
}: {
  className?: string;
  dark?: boolean;
  align?: "left" | "right";
}) {
  const locale = useLocale();
  const router = useRouter();
  const pathname = usePathname();
  const [isPending, startTransition] = useTransition();
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    function onClick(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    }
    function onKey(e: KeyboardEvent) {
      if (e.key === "Escape") setOpen(false);
    }
    document.addEventListener("mousedown", onClick);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onClick);
      document.removeEventListener("keydown", onKey);
    };
  }, []);

  function switchTo(next: string) {
    setOpen(false);
    if (next === locale) return;
    track("language_switched", { from: locale, to: next });
    startTransition(() => {
      router.replace(pathname, { locale: next });
    });
  }

  return (
    <div ref={ref} className={cn("relative", isPending && "opacity-60", className)}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-label={`${locale.toUpperCase()} - ${NAMES[locale] ?? locale}`}
        className={cn(
          "inline-flex items-center gap-1.5 rounded-md px-2 py-1.5 text-sm font-semibold transition-colors",
          dark
            ? "text-white/80 hover:bg-white/10 hover:text-white"
            : "text-slate hover:bg-mist hover:text-navy",
        )}
      >
        <Flag locale={locale} className="h-3.5 w-5 rounded-[2px] shadow-[0_0_0_1px_rgba(0,0,0,0.08)]" />
        <span className="uppercase">{locale}</span>
        <ChevronDown className={cn("size-3.5 transition-transform", open && "rotate-180")} />
      </button>

      {open && (
        <ul
          role="listbox"
          className={cn(
            "absolute z-50 mt-1.5 min-w-[168px] overflow-hidden rounded-xl border border-line bg-white p-1 shadow-soft",
            align === "left" ? "left-0" : "right-0",
          )}
        >
          {routing.locales.map((l) => {
            const active = l === locale;
            return (
              <li key={l} role="option" aria-selected={active}>
                <button
                  type="button"
                  onClick={() => switchTo(l)}
                  className={cn(
                    "flex w-full items-center gap-2.5 rounded-lg px-2.5 py-2 text-left text-sm transition-colors",
                    active ? "bg-mist font-semibold text-navy" : "text-slate hover:bg-mist hover:text-navy",
                  )}
                >
                  <Flag locale={l} className="h-3.5 w-5 rounded-[2px] shadow-[0_0_0_1px_rgba(0,0,0,0.08)]" />
                  <span className="flex-1">{NAMES[l] ?? l}</span>
                  {active && <Check className="size-4 text-brand" />}
                </button>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}
